<script setup lang="ts">
import { computed, nextTick, reactive, ref, useTemplateRef, watch } from 'vue';
import { uniqueId } from 'lodash-es';
import CircleCloseIcon from '@/assets/icons/shared/circle/close.svg';
import type { Option } from '@/modules/common/types/option.type.ts';
import { IdPrefix } from '@/modules/common/enums/id-prefix.ts';
import {
  validateCounterpartyLegalFields,
  type CounterpartyLegalFields,
} from '@/modules/counterparty/utils/counterparty-legal.validator.ts';
import { COUNTERPARTY_DIGITS_MASK } from '@/modules/counterparty/counterparty-master/consts/digits-mask.ts';
import {
  LEGAL_DATA_FOCUS_SHADOW_PX,
  LEGAL_DATA_MAX_HEIGHT_PX,
  LEGAL_DATA_NAME_MIN_HEIGHT_PX,
} from '@/modules/counterparty/counterparty-master/consts/legal-data-layout.ts';
import { CounterpartyOptionsUtil } from '@/modules/counterparty/utils/counterparty-options.util.ts';
import { ValueUtil } from '@/modules/common/utils/value.util.ts';
import { useFormTooltips } from '@/modules/ui/composables/use-form-tooltips.ts';
import {
  COUNTERPARTY_INN_DUPLICATE_ERROR,
  COUNTERPARTY_NAME_DUPLICATE_ERROR,
  useCounterpartyInnDuplicate,
  useCounterpartyNameDuplicate,
} from '@/modules/counterparty/counterparty-master/composables/use-counterparty-duplicate.ts';
import CounterpartyNameMatchIndicator from '@/modules/counterparty/counterparty-master/components/CounterpartyNameMatchIndicator.vue';
import UiButton from '@/modules/ui/components/ui/UiButton.vue';
import UiCombobox from '@/modules/ui/components/ui/UiCombobox.vue';
import UiField from '@/modules/ui/components/ui/UiField.vue';
import UiInput from '@/modules/ui/components/ui/UiInput.vue';
import UiLoader from '@/modules/ui/components/ui/UiLoader.vue';
import UiTextarea from '@/modules/ui/components/ui/UiTextarea.vue';

type DigitsField = 'inn' | 'kpp' | 'ogrn' | 'okpo' | 'okato';
type TouchedField = 'name' | DigitsField;

const PLACEHOLDERS = {
  name: 'counterparty.master.steps.name.field.orgName',
  legalForm: 'counterparty.master.steps.name.legalData.legalForm',
  inn: 'counterparty.master.steps.name.legalData.inn',
  kpp: 'counterparty.master.steps.name.legalData.kpp',
  ogrn: 'counterparty.master.steps.name.legalData.ogrn',
  okpo: 'counterparty.master.steps.name.legalData.okpo',
  okato: 'counterparty.master.steps.name.legalData.okato',
} as const;

const name = defineModel<string>('name', { required: true });
const legalForm = defineModel<Option | null>('legalForm', { required: true });
const inn = defineModel<string>('inn', { required: true });
const kpp = defineModel<string>('kpp', { required: true });
const ogrn = defineModel<string>('ogrn', { required: true });
const okpo = defineModel<string>('okpo', { required: true });
const okato = defineModel<string>('okato', { required: true });
const valid = defineModel<boolean>('valid', { default: false });

const {
  loading = false,
  showErrors = false,
  showLegalEntityFields = true,
} = defineProps<{
  loading?: boolean;
  showErrors?: boolean;
  showLegalEntityFields?: boolean;
}>();

const emit = defineEmits<{
  clear: [];
  change: [];
}>();

const { tooltipsDisabled, onInput } = useFormTooltips();
const {
  isDuplicate: isNameDuplicate,
  isChecked: isNameChecked,
  isLoading: isNameLoading,
  similarMatches: nameMatches,
  check: checkNameDuplicate,
  reset: resetNameDuplicate,
} = useCounterpartyNameDuplicate(name, {
  showErrors: () => showErrors,
});
const {
  isDuplicate: isInnDuplicate,
  isChecked: isInnChecked,
  check: checkInnDuplicate,
  reset: resetInnDuplicate,
} = useCounterpartyInnDuplicate(inn, {
  showErrors: () => showErrors,
});
const fieldsEl = useTemplateRef<HTMLElement>('fields');
const nameFocused = ref(false);
const innFocused = ref(false);
const layoutStyle = {
  '--legal-data-max-height': `${LEGAL_DATA_MAX_HEIGHT_PX}px`,
  '--legal-data-focus-shadow': `${LEGAL_DATA_FOCUS_SHADOW_PX}px`,
};

let ignoreScrollBlur = false;

const fieldIds: Record<TouchedField | 'legalForm', string> = {
  name: uniqueId(IdPrefix.Control),
  legalForm: uniqueId(IdPrefix.Control),
  inn: uniqueId(IdPrefix.Control),
  kpp: uniqueId(IdPrefix.Control),
  ogrn: uniqueId(IdPrefix.Control),
  okpo: uniqueId(IdPrefix.Control),
  okato: uniqueId(IdPrefix.Control),
};

const touched = reactive<Record<TouchedField, boolean>>({
  name: false,
  inn: false,
  kpp: false,
  ogrn: false,
  okpo: false,
  okato: false,
});

const legalFields = computed((): CounterpartyLegalFields => ({
  name: name.value,
  inn: inn.value,
  kpp: kpp.value,
  ogrn: ogrn.value,
  okpo: okpo.value,
  okato: okato.value,
}));

const hasAnyValue = computed(
  () =>
    Boolean(name.value.trim()) ||
    Boolean(legalForm.value) ||
    Boolean(inn.value.trim()) ||
    Boolean(kpp.value.trim()) ||
    Boolean(ogrn.value.trim()) ||
    Boolean(okpo.value.trim()) ||
    Boolean(okato.value.trim())
);

const errors = computed(() =>
  validateCounterpartyLegalFields(legalFields.value, {
    requireName: true,
    validateKpp: showLegalEntityFields,
    validateOkpoOkato: showLegalEntityFields,
  })
);

watch(
  [errors, isNameDuplicate, isNameChecked, isInnDuplicate, isInnChecked],
  () => {
    valid.value =
      !Object.keys(errors.value).length &&
      !isNameDuplicate.value &&
      isNameChecked.value &&
      !isInnDuplicate.value &&
      isInnChecked.value;
  },
  { immediate: true }
);

watch(
  () => showLegalEntityFields,
  (show) => {
    if (show) {
      return;
    }

    legalForm.value = null;
    kpp.value = '';
    okpo.value = '';
    okato.value = '';
    touched.kpp = false;
    touched.okpo = false;
    touched.okato = false;
  }
);

watch(legalFields, (value, prev) => {
  if (!prev) {
    return;
  }

  let changed = false;

  (Object.keys(touched) as TouchedField[]).forEach((field) => {
    const next = value[field];
    const previous = prev[field];
    const fieldChanged = field === 'name' ? next.trim() !== previous.trim() : next !== previous;

    if (fieldChanged) {
      touched[field] = false;
      changed = true;
    }
  });

  if (changed) {
    emit('change');
  }
});

watch(legalForm, (value, prev) => {
  if (value?.code === prev?.code && value?.label === prev?.label) {
    return;
  }

  emit('change');
});

watch(
  name,
  async (value, prev) => {
    if (prev !== undefined && value.trim() === prev.trim()) {
      return;
    }

    if (!value.trim() || nameFocused.value) {
      return;
    }

    await nextTick();

    if (nameFocused.value) {
      return;
    }

    touched.name = true;
    await checkNameDuplicate();
  },
  { immediate: true }
);

watch(
  inn,
  async (value, prev) => {
    const next = value.replace(/\D/gu, '');
    const previous = (prev ?? '').replace(/\D/gu, '');
    if (prev !== undefined && next === previous) {
      return;
    }

    if (!next || innFocused.value) {
      return;
    }

    await nextTick();

    if (innFocused.value) {
      return;
    }

    touched.inn = true;
    await checkInnDuplicate();
  },
  { immediate: true }
);

function resetTouched() {
  touched.name = false;
  touched.inn = false;
  touched.kpp = false;
  touched.ogrn = false;
  touched.okpo = false;
  touched.okato = false;
}

function markTouched(field: TouchedField) {
  touched[field] = true;
}

function fieldError(field: TouchedField) {
  if (field === 'name' && isNameDuplicate.value && isNameChecked.value) {
    return COUNTERPARTY_NAME_DUPLICATE_ERROR;
  }

  if (!(touched[field] || showErrors)) {
    return null;
  }

  if (field === 'name') {
    return errors.value.name ?? null;
  }

  if (field === 'inn') {
    return errors.value.inn ?? (isInnDuplicate.value ? COUNTERPARTY_INN_DUPLICATE_ERROR : null);
  }

  return errors.value[field] ?? null;
}

function onClear() {
  resetTouched();
  resetNameDuplicate();
  resetInnDuplicate();
  emit('clear');
}

function onNameFocus() {
  nameFocused.value = true;
}

async function onNameBlur() {
  nameFocused.value = false;
  markTouched('name');
  await checkNameDuplicate();
}

function onInnFocus() {
  innFocused.value = true;
}

async function onInnBlur() {
  innFocused.value = false;
  markTouched('inn');
  await checkInnDuplicate();
}

function blurActiveField() {
  const active = document.activeElement;
  if (!(active instanceof HTMLElement) || !fieldsEl.value?.contains(active)) {
    return;
  }

  active.blur();
}

function onFieldsScroll() {
  if (ignoreScrollBlur) {
    return;
  }

  blurActiveField();
}

function onFieldsFocusIn(event: FocusEvent) {
  const container = fieldsEl.value;
  const target = event.target;
  if (!container || !(target instanceof HTMLElement) || !container.contains(target)) {
    return;
  }

  const field = target.closest('.ui-field');
  if (!(field instanceof HTMLElement) || !container.contains(field)) {
    return;
  }

  const inset = LEGAL_DATA_FOCUS_SHADOW_PX;
  const containerRect = container.getBoundingClientRect();
  const fieldRect = field.getBoundingClientRect();
  const visibleTop = containerRect.top + inset;
  const visibleBottom = containerRect.bottom - inset;
  const visibleHeight = visibleBottom - visibleTop;

  ignoreScrollBlur = true;

  if (fieldRect.height > visibleHeight) {
    container.scrollTop += fieldRect.top - visibleTop;
  } else if (fieldRect.top < visibleTop) {
    container.scrollTop -= visibleTop - fieldRect.top;
  } else if (fieldRect.bottom > visibleBottom) {
    container.scrollTop += fieldRect.bottom - visibleBottom;
  }

  requestAnimationFrame(() => {
    ignoreScrollBlur = false;
  });
}
</script>

<template>
  <section
    class="legal-data-block"
    :class="{ _loading: loading }"
    :style="layoutStyle"
  >
    <div class="header">
      <h3 class="title">{{ $t('counterparty.master.steps.name.legalData.title') }}</h3>

      <UiButton
        v-if="hasAnyValue"
        class="clear"
        color="black"
        mode="plain"
        size="s"
        @click="onClear"
      >
        <CircleCloseIcon v-prefix />
        <span>{{ $t('counterparty.master.steps.name.legalData.clear') }}</span>
      </UiButton>
    </div>

    <div
      ref="fields"
      class="fields"
      @focusin="onFieldsFocusIn"
      @scroll="onFieldsScroll"
    >
      <div
        class="name-field"
        :class="{ '_with-indicator': isNameLoading || nameMatches.length }"
      >
        <UiField
          :id="fieldIds.name"
          stretch
          :error="fieldError('name')"
          with-tooltip
          :tooltip="ValueUtil.placeholderTooltip(PLACEHOLDERS.name, name)"
          :tooltip-disabled="tooltipsDisabled"
        >
          <UiTextarea
            :id="fieldIds.name"
            v-model="name"
            class="full-width"
            :placeholder="PLACEHOLDERS.name"
            :error="!!fieldError('name')"
            :max-length="255"
            :rows="2"
            :auto-grow-min-height="LEGAL_DATA_NAME_MIN_HEIGHT_PX"
            auto-grow
            no-resize
            @focus="onNameFocus"
            @blur="onNameBlur"
            @update:model-value="onInput"
          />
        </UiField>

        <CounterpartyNameMatchIndicator
          :loading="isNameLoading"
          :matches="nameMatches"
        />
      </div>

      <div class="grid">
        <UiField
          v-if="showLegalEntityFields"
          :id="fieldIds.legalForm"
          stretch
          with-tooltip
          :tooltip="ValueUtil.placeholderTooltip(PLACEHOLDERS.legalForm, legalForm)"
          :tooltip-disabled="tooltipsDisabled"
        >
          <UiCombobox
            v-model="legalForm"
            :placeholder="PLACEHOLDERS.legalForm"
            :options="CounterpartyOptionsUtil.legalForms"
            expanded
            no-clear
          />
        </UiField>

        <UiField
          :id="fieldIds.inn"
          stretch
          :error="fieldError('inn')"
          with-tooltip
          :tooltip="ValueUtil.placeholderTooltip(PLACEHOLDERS.inn, inn)"
          :tooltip-disabled="tooltipsDisabled"
        >
          <UiInput
            :id="fieldIds.inn"
            v-model="inn"
            :placeholder="PLACEHOLDERS.inn"
            :mask="COUNTERPARTY_DIGITS_MASK.inn"
            inputmode="numeric"
            :error="!!fieldError('inn')"
            :max-length="12"
            no-clear
            @focus="onInnFocus"
            @blur="onInnBlur"
            @update:model-value="onInput"
          />
        </UiField>

        <UiField
          v-if="showLegalEntityFields"
          :id="fieldIds.kpp"
          stretch
          :error="fieldError('kpp')"
          with-tooltip
          :tooltip="ValueUtil.placeholderTooltip(PLACEHOLDERS.kpp, kpp)"
          :tooltip-disabled="tooltipsDisabled"
        >
          <UiInput
            :id="fieldIds.kpp"
            v-model="kpp"
            :placeholder="PLACEHOLDERS.kpp"
            :mask="COUNTERPARTY_DIGITS_MASK.kpp"
            inputmode="numeric"
            :error="!!fieldError('kpp')"
            :max-length="9"
            no-clear
            @blur="markTouched('kpp')"
            @update:model-value="onInput"
          />
        </UiField>

        <UiField
          :id="fieldIds.ogrn"
          stretch
          :error="fieldError('ogrn')"
          with-tooltip
          :tooltip="ValueUtil.placeholderTooltip(PLACEHOLDERS.ogrn, ogrn)"
          :tooltip-disabled="tooltipsDisabled"
        >
          <UiInput
            :id="fieldIds.ogrn"
            v-model="ogrn"
            :placeholder="PLACEHOLDERS.ogrn"
            :mask="COUNTERPARTY_DIGITS_MASK.ogrn"
            inputmode="numeric"
            :error="!!fieldError('ogrn')"
            :max-length="15"
            no-clear
            @blur="markTouched('ogrn')"
            @update:model-value="onInput"
          />
        </UiField>

        <UiField
          v-if="showLegalEntityFields"
          :id="fieldIds.okpo"
          stretch
          :error="fieldError('okpo')"
          with-tooltip
          :tooltip="ValueUtil.placeholderTooltip(PLACEHOLDERS.okpo, okpo)"
          :tooltip-disabled="tooltipsDisabled"
        >
          <UiInput
            :id="fieldIds.okpo"
            v-model="okpo"
            :placeholder="PLACEHOLDERS.okpo"
            :mask="COUNTERPARTY_DIGITS_MASK.okpo"
            inputmode="numeric"
            :error="!!fieldError('okpo')"
            :max-length="10"
            no-clear
            @blur="markTouched('okpo')"
            @update:model-value="onInput"
          />
        </UiField>

        <UiField
          v-if="showLegalEntityFields"
          :id="fieldIds.okato"
          stretch
          :error="fieldError('okato')"
          with-tooltip
          :tooltip="ValueUtil.placeholderTooltip(PLACEHOLDERS.okato, okato)"
          :tooltip-disabled="tooltipsDisabled"
        >
          <UiInput
            :id="fieldIds.okato"
            v-model="okato"
            :placeholder="PLACEHOLDERS.okato"
            :mask="COUNTERPARTY_DIGITS_MASK.okato"
            inputmode="numeric"
            :error="!!fieldError('okato')"
            :max-length="11"
            no-clear
            @blur="markTouched('okato')"
            @update:model-value="onInput"
          />
        </UiField>
      </div>
    </div>

    <UiLoader
      v-if="loading"
      color="transparent"
    />
  </section>
</template>

<style scoped lang="scss">
.legal-data-block {
  position: relative;
  display: flex;
  flex: 0 1 auto;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  max-height: var(--legal-data-max-height);
  padding: 12px 12px 0;
  border: var(--border-gray-200);
  border-radius: 16px;
  overflow: hidden;
  background-color: var(--color-white);

  &._loading {
    pointer-events: none;

    .header,
    .fields {
      opacity: 0.4;
    }
  }
}

.header {
  position: relative;
  z-index: 1;
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 0;
  padding-bottom: 4px;
  background-color: var(--color-white);
}

.fields {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
  min-height: 0;
  margin-inline: calc(-1 * var(--legal-data-focus-shadow));
  padding: var(--legal-data-focus-shadow);
  overflow-x: hidden;
  overflow-y: auto;
  scroll-padding: var(--legal-data-focus-shadow);
}

.name-field {
  position: relative;
  flex: 0 0 auto;

  &._with-indicator {
    :deep(.ui-textarea) {
      padding-right: 32px;
    }
  }

  :deep(.ui-textarea._auto-grow) {
    max-height: none;
  }
}

.title {
  margin: 0;
  font-size: var(--font-size-l);
  line-height: var(--line-height-l);
  font-weight: var(--font-weight-bold);
}

.clear {
  flex-shrink: 0;
  margin: -4px -8px -4px 0;
}

.full-width {
  width: 100%;
  min-width: 0;
}

.grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 12px;
  align-items: start;
  min-width: 0;

  > :deep(*) {
    min-width: 0;
  }
}
</style>
